
import React, { useEffect, useMemo, useState, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SideModal from '../../containers/side-modal';
import { getStorageKey } from './smartTable.utils';
import type {
  SmartColumn, SmartTableProps, SortState, IFilterControlState, IActiveColumn
} from './smartTable.types';
import { EmptyFilterControlState } from './smartTable.types';
import { ColumnDefinition } from '../ColumnDefinitionNew';
import SmartTableToolbar from './SmartTableToolbar';
import CustomForm from '../../containers/custom-form';
import NumericField from '../../controls/fields/numericFieldNew';
import SelectField from '../../controls/fields/selectFieldNew';
import TextField from '../../controls/fields/textFieldNew';
import { HttpService } from '../../../services/HttpService';
import { DateHelper } from '../../../helpers/DateHelper';
import SmartTableLoading from './SmartTableLoading';
import SmartTableBodyGrid from './SmartTableBodyGrid';
import SmartTableBodyList from './SmartTableBodyList';
import SmartTablePaginator from './SmartTablePaginator';
import ColumnSelector from './ColumnSelector';
import './SmartTable.scss';
import { ReactComponent as IconSearch } from "itd-common/assets/icons/search-icon.svg";

const T_PREFIX: string = 'shared.smartTable.';
const COMPLETESTATEVALUES = ["2", "3", "4", "13", "14", "16"];

function getFieldValue(row: any, path: string): any {
  const parts = path.split('.');
  let acc = row;
  for (const p of parts) { acc = acc?.[p]; if (acc === undefined || acc === null) break; }
  return acc;
}

function deriveLabel(row: any, def: ColumnDefinition): any {
  if (def.filterKind === 'select' || def.filterKind === 'boolean' || def.filterKind === 'image') {
    if (def.mapValue) {
      const value = def.mapValue ? def.mapValue(row) : getFieldValue(row, def.key);
      return value;
    }
  }
  const raw = def.mapValue ? def.mapValue(row) : getFieldValue(row, def.key);
  return raw != null ? (React.isValidElement(raw as any) ? String((raw as any).props?.children ?? '') : String(raw)) : '';
}

function deriveValue(row: any, def: ColumnDefinition): any {
  return String(row[def?.key]);
}

const SmartTable: React.FC<SmartTableProps> = ({
  data, config, columnDefinitions, onDownload, onDownloadOptions, clearFilters, openRangeFilter, extraFilter, dateRangeType
}) => {
  const { t } = useTranslation();
  const mode = config?.mode ?? 'grid';
  const initialPageSize = config?.pageSize ?? 12;
  const defaultVisibleCount = config?.defaultVisibleCount ?? 5;

  /** ───────── Búsqueda realizada ───────── */
  const [hasSearched, setHasSearched] = useState(() => data.length > 0);
  const isFirstDataRender = useRef(true);
  useEffect(() => {
    if (isFirstDataRender.current) {
      isFirstDataRender.current = false;
      return;
    }
    setHasSearched(true);
  }, [data]);

  /** ───────── Columnas visibles / definición ───────── */
  const allColumns: SmartColumn[] = useMemo(() => {
    const visibleDefs = (columnDefinitions ?? []).filter((d) => !d.hidden);
    const mapType = (defType?: string): any => {
      switch (defType) {
        case 'number': return 'number';
        case 'date': return 'date';
        case 'boolean': return 'boolean';
        case 'percentage': return 'number';
        case 'currency': return 'number';
        case 'checkbox': return 'boolean';
        default: return 'text';
      }
    };
    return visibleDefs.map((d) => ({ key: d.key, label: d.label ?? d.key, type: mapType(d.type), def: d }));
  }, [columnDefinitions]);

  /** ───────── Loading indicator ───────── */
  const [isLoading, setIsLoading] = useState(HttpService.isLoading());
  useEffect(() => {
    const unsubscribe = HttpService.onLoadingChange(setIsLoading);
    return () => unsubscribe();
  }, []);

  /** ───────── Persistencia de columnas visibles ───────── */
  const storageKey = getStorageKey(config);
  const actionKeys = useMemo(() => allColumns.filter((c) => c.def?.type === 'action').map((c) => c.key), [allColumns]);
  const nonActionColumns = useMemo(() => allColumns.filter((c) => c.def?.type !== 'action'), [allColumns]);
  const [visibleKeys, setVisibleKeys] = useState<string[]>(() => { // se cargan las columnas con esto
    try {
      const raw = localStorage.getItem(storageKey);
      const persisted = raw ? JSON.parse(raw) : null;
      if (config?.defaultColumns) {
        const sanitized = config?.defaultColumns.filter((k: string) => nonActionColumns.some((c) => c.key === k));
        if (sanitized.length > 0) return sanitized;
      } else if (persisted && Array.isArray(persisted) && persisted.length > 0) {
        const sanitized = persisted.filter((k: string) => nonActionColumns.some((c) => c.key === k));
        if (sanitized.length > 0) return sanitized;
      }
    } catch { }
    let countVisibleColumn = config?.defaultColumns ? config?.defaultColumns.length - 1 : defaultVisibleCount; //si vienen defaultColumns devolvemos esa cantidad, sino la cantidad definida default
    return nonActionColumns.slice(0, countVisibleColumn).map((c) => c.key);
  });
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(visibleKeys));
      setShowModal(false);
    } catch { }
  }, [visibleKeys, storageKey]);

  /** ───────── Paginación por ventanas (bloques dinámicos) ───────── */
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [activePage, setActivePage] = useState<number>(1);

  // Índice de ventana actual (0-based) provisto por config.paginator
  const mainPage = config?.paginator?.mainPage ?? 0;

  // Páginas esperadas por ventana (estables para numeración)
  const [expectedWindowPages, setExpectedWindowPages] = useState<number>(Math.max(1, (config?.paginator?.quantity ?? 3)));


  // Tamaño de bloque esperado: fijo por config o derivado
  const expectedBlockSize = config?.blockSize ?? (rowsPerPage * expectedWindowPages);

  // Tamaño de bloque efectivo (dinámico, sin contar sentinel)
  const [blockSize, setBlockSize] = useState<number>(expectedBlockSize);

  // Sincronizar blockSize cuando cambia densidad o quantity/blockSize config
  useEffect(() => {
    const nextBlock = config?.blockSize ?? (rowsPerPage * expectedWindowPages);
    setBlockSize(nextBlock);
  }, [rowsPerPage, expectedWindowPages, config?.blockSize]);

  // Ajuste por data/sentinel (N+1): recalcula con cada carga
  // useEffect(() => {
  //   // const next = data.length > blockSize;
  //   // const bs = next ? (data.length - 1) : data.length; // ignorar sentinel si hay
  //   // setBlockSize(Math.max(1, bs));
  //   if(!hasNext){
  //     setExpectedWindowPages((data?.length/rowsPerPage));
  //   }
  // }, [data, rowsPerPage]);

  // Páginas efectivas del bloque actual (dinámico)
  const actualWindowPages = Math.max(1, Math.ceil(blockSize / rowsPerPage));

  // Inicio absoluto de la ventana (1-based)
  const windowStart = useMemo(() => {
    return (mainPage === 0 ? 1 : mainPage === 1 ? (expectedWindowPages + 1) : (mainPage * expectedWindowPages) + 1);
  }, [mainPage, expectedWindowPages]);


  // Array de páginas visibles (consecutivo, longitud dinámica)
  const pages: number[] = useMemo(
    () => Array.from({ length: actualWindowPages }, (_, i) => windowStart + i),
    [actualWindowPages, windowStart]
  );

  // Flag para saber si estamos cruzando de ventana (prev/next)
  const [isCrossingWindow, setIsCrossingWindow] = useState(false);

  // hasNext (N+1): habilita mover a la ventana siguiente si vino sentinel
  const hasNext = data.length > blockSize;

  /** ───────── Navegación de ventanas (bloques) ───────── */
  const onPrevWindow = () => {
    const fn = config?.paginator?.modifyMainPage;
    if (fn && mainPage > 0) {
      setIsCrossingWindow(true);
      fn(-1);
    }
  };
  const onNextWindow = () => {
    const fn = config?.paginator?.modifyMainPage;
    if (fn && hasNext) {
      setIsCrossingWindow(true);
      fn(+1);
    }
  };

  // Reposicionar al PRIMER número del nuevo bloque cuando pages quedó estable (evita flicker)
  useLayoutEffect(() => {
    if (!isCrossingWindow) return;
    const first = pages[0] ?? windowStart;
    if (activePage !== first) setActivePage(first);
    setIsCrossingWindow(false);
  }, [isCrossingWindow, pages, windowStart, activePage]);

  // Clamp defensivo (red de seguridad): si la página activa no existe, ir al PRIMERO
  useEffect(() => {
    const first = pages[0] ?? windowStart;
    if (!pages.includes(activePage)) setActivePage(first);
  }, [pages, activePage, windowStart]);

  /** ───────── QuickFilter + Sorting ───────── */
  const [activeColumnKey, setActiveColumnKey] = useState<IActiveColumn>({ selectedValue: "" });

  const selectedColumn = useMemo(() => {
    return allColumns.find(
      (c) => c.key === activeColumnKey.selectedValue
    );
  }, [activeColumnKey.selectedValue, allColumns]);

  const [filterControlState, setFilterControlState] = useState<IFilterControlState>({
    textValue: "", selectedValue: "", numberValue: "",
  });
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    if (!selectedColumn) { setOptions([]); return; }
    if (selectedColumn.def.filterKind === 'select'
      || selectedColumn.def.filterKind === 'boolean'
      || selectedColumn.def.filterKind === 'image') {
      const map = new Map<string, string>();
      for (const row of data) {
        const value = deriveValue(row, selectedColumn.def);
        if (!value) continue;
        if (!map.has(value)) map.set(value, deriveLabel(row, selectedColumn.def));
      }
      let opts: { value: string; label: string }[] = [];

      if (selectedColumn.def.filterKind === "image") {
        if (selectedColumn.def.key === "state") {
          //unificamos todos los estates completa a 2
          const normalized = Array.from(map.entries()).map(([value, label]) => {
            const newValue = COMPLETESTATEVALUES.includes(value) ? COMPLETESTATEVALUES[0] : value;
            return { value: newValue, label };
          });
          // eliminamos los duplicados
          const values = normalized.filter(
            (item, index, self) =>
              index === self.findIndex((i) => i.value === item.value)
          );
          opts = values;
        } else {
          opts = Array.from(map.entries()).map(([value, label]) => ({ value, label }));
        }
      } else {
        opts = Array.from(map.entries()).map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
      }
      setOptions(opts);
    } else {
      setOptions([]);
    }
  }, [data, selectedColumn]);

  const [sortState, setSortState] = useState<SortState>({});
  const onHeaderSort = (key: string) =>
    setSortState((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return {};
    });

  /** ───────── Filtrar + Ordenar sobre el lote actual (bloque) ───────── */
  const filteredData = useMemo(() => {
    let out = data;
    if (selectedColumn) {
      const def = selectedColumn.def;
      const predicate = (row: any): boolean => {
        const raw = def.mapValue ? def.mapValue(row) : getFieldValue(row, def.key);
        switch (def.filterKind) {
          case 'number': {
            let num: any = null;
            if (raw) {
              num = raw.replace("$", "").replace(" ", "");
            }
            if (num) {
              num = num.toLowerCase();
              return num.includes(filterControlState.numberValue.toString().toLowerCase());
            }
            return false;
          }
          case 'select':
          case 'image':
          case 'boolean': {
            if (!filterControlState.selectedValue) return true;
            const v = deriveValue(row, def);
            if (def.key === 'state' && filterControlState.selectedValue === COMPLETESTATEVALUES[0]) { // si es filtro estado y tiene seleccion de estado completa
              return COMPLETESTATEVALUES.includes(v); //si contiene uno de los valores de estado completa lo retorna y unificamos los valores
            }
            return v === filterControlState.selectedValue;
          }
          default: {
            if (!filterControlState.textValue) return true;
            const s = (raw ?? '').toString().toLowerCase();
            return s.includes(filterControlState.textValue.toLowerCase());
          }
        }
      };
      out = out.filter(predicate);
    }
    if (sortState.key && sortState.direction) {
      const key = sortState.key;
      const dir = sortState.direction === 'asc' ? 1 : -1;
      const col = allColumns.find((c) => c.key === key);
      const accessor = (row: any) => {
        const def: any = col?.def;
        if (typeof def?.sortAccessor === 'function') return def.sortAccessor(row);
        return row[key];
      };
      out = [...out].sort((a, b) => {
        const va = accessor(a), vb = accessor(b);
        try {
          if (col?.type === 'date') { const ta = new Date(va).getTime(); const tb = new Date(vb).getTime(); return (ta - tb) * dir; }
          if (col?.type === 'number') { const na = Number(va), nb = Number(vb); return (na - nb) * dir; }
          return String(va).localeCompare(String(vb)) * dir;
        } catch { return 0; }
      });
    }
    return out;
  }, [data, selectedColumn, filterControlState, sortState, allColumns]);

  /** ───────── Slicing relativo al array de páginas del bloque ───────── */
  const pageIndexInWindow = Math.max(0, activePage - windowStart); // activo (1-based) - inicio ⇒ índice 0-based
  const currentIndex = pageIndexInWindow * rowsPerPage;
  const currentRows = useMemo(
    () => filteredData.slice(currentIndex, currentIndex + rowsPerPage),
    [filteredData, currentIndex, rowsPerPage]
  );

  /** ───────── Total UI ajustado ───────── */
  const total = useMemo(() => {
    // Ítems previos al inicio de esta ventana bajo la densidad actual
    const previousItems = (windowStart - 1) * rowsPerPage;

    if (hasNext) {
      // Intermedia: total lógico consistente hasta el fin de la ventana actual
      return previousItems + (actualWindowPages * rowsPerPage);
    }

    // Última: total exacto = previos + tamaño efectivo del bloque
    return previousItems + blockSize;
  }, [hasNext, windowStart, rowsPerPage, actualWindowPages, blockSize]);

  /** ───────── QuickFilter UI ───────── */
  const [showModal, setShowModal] = useState(false);

  const filterControl = (
    data.length > 0 ? <CustomForm model={[filterControlState, setFilterControlState]} >
      {selectedColumn?.def.filterKind === 'number' ? (
        <div className="st-af__range">
          <NumericField
            attr="numberValue"
            placeholder={`Buscar ${selectedColumn?.def.label?.toLowerCase()}`}
            label=""
          />
        </div>
      ) : (selectedColumn?.def.filterKind === 'select'
        || selectedColumn?.def.filterKind === 'boolean'
        || selectedColumn?.def.filterKind === 'image') ? (
        <SelectField
          attr="selectedValue"
          label=""
          placeholder={t(`${T_PREFIX}select`)}
          menuPosition="fixed"
          options={options.map(o => ({ key: o.value, value: o.value, label: o.label }))}
        />
      ) : (selectedColumn?.key !== "" || (selectedColumn?.def && selectedColumn?.def.filterKind === 'string')) ? (
        <TextField
          attr="textValue"
          placeholder={`Buscar ${selectedColumn?.def.label?.toLowerCase()}`}
          label=""
          className="mt-0"
          prefix={<IconSearch />}
        />
      ) : (
        undefined
      )}
    </CustomForm> : undefined
  );

  const visibleColumns: SmartColumn[] = useMemo(() => {
    const selected = nonActionColumns.filter((c) => visibleKeys.includes(c.key));
    const actions = allColumns.filter((c) => actionKeys.includes(c.key));
    return [...selected, ...actions];
  }, [nonActionColumns, visibleKeys, allColumns, actionKeys]);

  return (
    <div className="smart-table st-card">
      <SmartTableToolbar
        title={config?.title ?? t(`${T_PREFIX}title`)}
        onCustomize={() => setShowModal(true)}
        onDownload={onDownload}
        onDownloadOptions={onDownloadOptions}
        model={[activeColumnKey, setActiveColumnKey]}
        onActiveColumnChange={() => setFilterControlState(EmptyFilterControlState)}
        filterControl={config?.filterControl ?? filterControl}
        visibleColumns={visibleColumns}
        countData={data.length}
        isCustomizeTableAvailable={config?.isCustomizeTableAvailable ?? true}
        extraFilter={extraFilter}
        dateRangeType={dateRangeType}
      />

      {isLoading ? (
        <SmartTableLoading />
      ) : (
        mode === 'grid' ? (
          <SmartTableBodyGrid
            visibleColumns={visibleColumns}
            pageData={currentRows}
            hasSearched={hasSearched}
            clearFilters={clearFilters}
            openRangeFilter={openRangeFilter}
            formatValue={(row: any, col: SmartColumn): React.ReactNode => {
              const def: ColumnDefinition = col.def;
              const raw = def.mapValue ? def.mapValue(row) : row[col.key];
              const isEmpty = raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '');
              if (def.type === 'action' && def.action) {
                const a: any = def.action;
                const handle = (e: any) => { e.preventDefault(); try { a.function?.(row); } catch { } };
                const el = typeof a.icon === 'function' ? a.icon(row) : (React.isValidElement(a.icon) ? a.icon : null);
                return el ? (<div className="div-action"><a href="#" className="st-action" onClick={handle}>{el}</a></div>) : '-';
              }
              if (isEmpty) return '-';
              switch (def.type) {
                case 'date': { return typeof raw === 'string' ? DateHelper.formatIsoDate(raw) : raw }
                case 'number': { const num = Number(raw); return Number.isNaN(num) ? String(raw) : String(num); }
                case 'percentage': { const num = Number(raw); return Number.isNaN(num) ? String(raw) : `${Intl.NumberFormat('es-AR', { minimumSignificantDigits: 3, maximumSignificantDigits: 3 }).format(num)}%`; }
                case 'currency': { return React.isValidElement(raw as any) ? String((raw as any).props?.children ?? '') : String(raw); }
                case 'currency-type': { const key = `currency-${raw}`; return t(key, String(raw)); }
                case 'boolean': { const b = String(raw).toLowerCase(); const yes = t('yes', 'Sí'), no = t('no', 'No'); return (b === 'true' || b === '1') ? yes : no; }
                case 'checkbox': { const checked = !!raw; return (<input type="checkbox" checked={checked} readOnly />); }
                default: { return React.isValidElement(raw as any) ? (raw as any) : String(raw); }
              }
            }}
            // Índice relativo correcto
            localStartIndex={currentIndex}
            sortState={sortState}
            onHeaderSort={onHeaderSort}
          />
        ) : (
          <SmartTableBodyList
            visibleColumns={visibleColumns}
            pageData={currentRows}
            formatValue={(row: any, col: SmartColumn) => {
              const raw = col.def.mapValue ? col.def.mapValue(row) : row[col.key];
              return String((col.def.type === 'date' && typeof raw === 'string') ? DateHelper.formatIsoDate(raw) : (raw ?? ''));
            }}
            localStartIndex={currentIndex}
            sortState={sortState}
            onHeaderSort={onHeaderSort}
          />
        )
      )}

      <SmartTablePaginator
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(v) => {
          setRowsPerPage(v);
          setExpectedWindowPages(config?.blockSize ? (config?.blockSize / v) : ((rowsPerPage * expectedWindowPages) / v));
          setActivePage(1);
          if (mainPage > 0)
            config?.paginator?.modifyMainPage(-mainPage);//Reseteo el mainPage
        }}
        total={total}
        // Array de páginas + estado
        pages={pages}
        activePage={activePage}
        onPageChange={(p) => setActivePage(p)}
        // Mover ventana (delta) + N+1
        onPrevWindow={onPrevWindow}
        onNextWindow={onNextWindow}
        hasNext={hasNext}
        // Para “X de Y”: cantidad de páginas visibles en la ventana actual
        mainPage={mainPage}
      />

      {showModal && (
        <SideModal title={t(`${T_PREFIX}customizeTable`)} show={showModal} onClose={() => setShowModal(false)}>
          <ColumnSelector allColumns={nonActionColumns} selectedKeys={visibleKeys} onChange={setVisibleKeys} defaultKeys={config?.defaultColumns} />
        </SideModal>
      )}
    </div>
  );
};

export default SmartTable;